import themeConfig from "./theme.json";

/**
 * Converts camelCase to kebab-case.
 * e.g. "primaryStrong" → "primary-strong"
 */
const toKebabCase = (str) =>
  str
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();

/**
 * Walks the theme object and injects every leaf value as a CSS custom property
 * on :root. This keeps theme.json as the single source of truth — CSS files
 * consume values via var(--color-primary), var(--spacing-lg), etc.
 */
const setCssVariablesFromTheme = (theme) => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  const walk = (obj, path = []) => {
    Object.entries(obj).forEach(([key, value]) => {
      const nextPath = [...path, key];

      if (value && typeof value === "object" && !Array.isArray(value)) {
        walk(value, nextPath);
        return;
      }

      if (typeof value !== "string" && typeof value !== "number") return;

      const [first, ...rest] = nextPath;
      let base;

      switch (first) {
        case "colors":
          base = "color";
          break;
        case "fonts":
          base = "font";
          break;
        case "borderRadius":
          base = "border-radius";
          break;
        case "spacing":
          base = "spacing";
          break;
        case "fontSize":
          base = "font-size";
          break;
        default:
          base = toKebabCase(first);
      }

      const restKebab = rest.map(toKebabCase);
      const varName = `--${[base, ...restKebab].filter(Boolean).join("-")}`;
      root.style.setProperty(varName, String(value));
    });
  };

  walk(theme);
};

/**
 * Initialize theme engine.
 *
 * 1. Injects CSS custom properties from theme.json → :root
 * 2. Returns a flat THEME object for JS consumers (ConfigProvider, etc.)
 *
 * Called once in index.js at app startup.
 */
export const initTheme = () => {
  const theme = themeConfig;

  setCssVariablesFromTheme(theme);

  const colors = theme.colors || {};
  const fonts = theme.fonts || {};
  const borderRadius = theme.borderRadius || {};
  const antd = theme.antd || {};

  return {
    // flattened color keys: THEME.primary, THEME.bgPage, etc.
    ...colors,
    colors,
    fonts,
    borderRadius,
    antd,
    raw: theme,
  };
};
